import { useEffect } from "react";
import {
  FieldMetadataForTypes,
  Issue,
  IssueStatus,
  IssueType,
} from "@adsk/acc-issues-types";
import { getIssueTypeEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueTypes";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { getAreAllRequiredFieldsFilled } from "../utils/data/issueRequiredFields";
import { useWatcherPropName } from "../utils/hooks/watchers/useWatcherPropName";

const issueCanChangeToType = ({
  issue,
  type,
  selectedStatus,
  watcherProp,
}: {
  issue: Issue;
  type: IssueType;
  selectedStatus?: IssueStatus;
  watcherProp: "watchers" | "watcherObjects";
}) => {
  if (!selectedStatus && issue.status === IssueStatus.Draft) {
    return true;
  }
  if (
    getAreAllRequiredFieldsFilled({
      issue,
      watcherProp,
      fieldsMetadata: type.fieldsMetadata ?? [],
    })
  ) {
    return true;
  }
  console.debug(
    `Issue ${issue.displayId} cannot change to type ${type.title} with status ${selectedStatus}`
  );
  return false;
};

/**
 * For bulk edit, we do not want to let users change issues that are missing required fields to draft,
 * or change issues that are missing required fields of a specific type to that type.
 *
 * As decided in collaboration with the design team, we will show a modal letting them know
 * that they cannot select that option (status or type) when they either
 * A) select that option
 * or B) with the option selected, selects an additional issue that has missing required fields.
 *
 * ... and then we will reset either the status or type field to empty
 * (we call `onRestrictedOptionSelected` to allow the parent component to handle this)
 *
 */
export const useRequiredFieldsRestrictions = ({
  selectedIssues,
  selectedTypeId,
  selectedStatus,
  onRestrictedTypeSelected,
  onRestrictedStatusSelected,
}: {
  selectedIssues: Issue[];
  selectedTypeId?: string;
  selectedStatus?: IssueStatus;
  onRestrictedTypeSelected: () => void;
  onRestrictedStatusSelected: () => void;
}) => {
  const issueTypeEntitiesMap = useSelector(getIssueTypeEntitiesMap);
  const depString = JSON.stringify(selectedIssues.map(issue => issue.id));
  const watcherProp = useWatcherPropName();
  useEffect(() => {
    console.debug("Checking for required fields restrictions...");
    if (!selectedStatus && !selectedTypeId) {
      console.debug("... neither type nor status selected: no restrictions");
      return;
    }
    if (selectedStatus === IssueStatus.Draft) {
      console.debug("... draft status selected: no restrictions");
      return;
    }
    if (selectedStatus) {
      const allSelectedIssuesCanChangeToStatus = selectedIssues.every(
        (issue: Issue) => {
          const issueType = issueTypeEntitiesMap[issue.issueTypeId!];
          if (!issueType) {
            // very unlikely to happen; issues should always have types
            return true;
          }
          return issueCanChangeToType({
            issue,
            type: issueType,
            selectedStatus,
            watcherProp,
          });
        }
      );
      if (allSelectedIssuesCanChangeToStatus) {
        console.debug(
          "... all selected issues have all required fields for status: no restrictions"
        );
        return;
      } else {
        console.debug("... restrictions due to status found! Alerting user.");
        onRestrictedStatusSelected();
        return;
      }
    } else if (selectedTypeId) {
      const selectedType = issueTypeEntitiesMap[selectedTypeId];
      if (
        !selectedType.fieldsMetadata ||
        selectedType.fieldsMetadata.every(
          ({ required }: FieldMetadataForTypes) => !required
        )
      ) {
        console.debug(
          "... selected type has no required fields: no restrictions"
        );
        return;
      }

      const allSelectedIssuesCanChangeToType = selectedIssues.every(
        (issue: Issue) => {
          return issueCanChangeToType({
            issue,
            type: selectedType,
            selectedStatus,
            watcherProp,
          });
        }
      );
      if (allSelectedIssuesCanChangeToType) {
        console.debug(
          "... all selected issues have all required fields for type: no restrictions"
        );
        return;
      } else {
        console.debug("... restrictions due to type found! Alerting user.");
        onRestrictedTypeSelected();
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depString, selectedStatus, selectedTypeId]);
};
